/**
 * The Torik Cloud service portals — a list of the apps a Torik hub can hand work
 * to: a stay booked, a shop order, a cleaning crew, a repair call-out.
 *
 * Mirrors the portal catalog the web app owns:
 *   - otto-web/src/lib/cloudAppSlugs.json  (slugs the portal hosts answer for)
 *   - otto-web/src/lib/cloudApps.tsx       (names + one-liners)
 *
 * The list is duplicated rather than imported: otto-ui is a separate SPA build and
 * cannot reach into otto-web's src. Keep the slugs in sync when a portal is added —
 * a slug that the web app does not know about resolves to no host.
 *
 * Deliberately NOT part of src/config/brand.ts: that file is generated by
 * `npm run brand:sync` (otto-ws) and local edits are overwritten.
 */

import { brand } from './brand';
import type { Bilingual } from './bilingual';

export interface ServicePortal {
  /** Subdomain label — `stay` answers on stay.torik.ai. */
  slug: string;
  /** Product name without the platform prefix, e.g. 'Stay'. A brand word — not translated. */
  name: string;
  /** One-liner under the name in the launcher (`tt` picks the half). */
  description: Bilingual;
  /** Material Symbols icon name. */
  icon: string;
  /** Tailwind classes for the tile icon — one tint per service family. */
  tint: string;
}

/** Every portal Torik Cloud runs, in the order the launcher shows them. */
export const SERVICE_PORTALS: readonly ServicePortal[] = [
  {
    slug: 'stay',
    name: 'Stay',
    description: {
      en: 'Hotels & apartments — direct bookings',
      sl: 'Hoteli in apartmaji — neposredne rezervacije',
    },
    icon: 'hotel',
    tint: 'bg-sky-50 text-sky-600 dark:bg-sky-500/10 dark:text-sky-300',
  },
  {
    slug: 'buy',
    name: 'Get',
    description: {
      en: 'Stores & cellars — products & orders',
      sl: 'Trgovine in kleti — izdelki in naročila',
    },
    icon: 'storefront',
    tint: 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-300',
  },
  {
    slug: 'eat',
    name: 'Eat',
    description: {
      en: 'Restaurants — menus, orders & tables',
      sl: 'Restavracije — meniji, naročila in mize',
    },
    icon: 'restaurant',
    tint: 'bg-orange-50 text-orange-600 dark:bg-orange-500/10 dark:text-orange-300',
  },
  {
    slug: 'fetch',
    name: 'Fetch',
    description: {
      en: 'Parcels, groceries & runs — collected and delivered',
      sl: 'Paketi, živila in dostave — prevzem in dostava',
    },
    icon: 'package_2',
    tint: 'bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-300',
  },
  {
    slug: 'drive',
    name: 'Drive',
    description: {
      en: 'Rides, transfers & chauffeurs — booked in the app',
      sl: 'Vožnje, transferji in šoferji — rezervirano v aplikaciji',
    },
    icon: 'directions_car',
    tint: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-300',
  },
  {
    slug: 'install',
    name: 'Install',
    description: {
      en: 'Certified installers — quotes & tracking',
      sl: 'Pooblaščeni monterji — ponudbe in sledenje',
    },
    icon: 'engineering',
    tint: 'bg-cyan-50 text-cyan-600 dark:bg-cyan-500/10 dark:text-cyan-300',
  },
  {
    slug: 'protect',
    name: 'Protect',
    description: {
      en: 'Monitored security — patrols & response',
      sl: 'Nadzorovano varovanje — obhodi in odziv',
    },
    icon: 'shield',
    tint: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300',
  },
  {
    slug: 'care',
    name: 'Care',
    description: {
      en: 'Home care & family support',
      sl: 'Nega na domu in podpora družini',
    },
    icon: 'volunteer_activism',
    tint: 'bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-300',
  },
  {
    slug: 'clean',
    name: 'Clean',
    description: {
      en: 'Cleaning — crews, schedules & proof',
      sl: 'Čiščenje — ekipe, urniki in dokazila',
    },
    icon: 'cleaning_services',
    tint: 'bg-teal-50 text-teal-600 dark:bg-teal-500/10 dark:text-teal-300',
  },
  {
    slug: 'fix',
    name: 'Fix',
    description: {
      en: 'Repairs & maintenance — work orders',
      sl: 'Popravila in vzdrževanje — delovni nalogi',
    },
    icon: 'build',
    tint: 'bg-slate-100 text-slate-600 dark:bg-slate-500/10 dark:text-slate-300',
  },
];

/** Base domain the portals live under, e.g. 'torik.ai'. */
const portalBaseDomain = brand.siteUrl.replace(/^https?:\/\//, '').replace(/\/+$/, '');

/**
 * How torik.ai groups and orders the services — the platform's own services
 * first (Install, Protect, Care, Clean, Fix), then what a venue books (Get,
 * Eat, Fetch, Drive, Stay). Mirrors `SERVICE_GROUPS` in
 * otto-web/src/lib/cloudApps.tsx; keep the two in step when a portal is added.
 */
export const SERVICE_GROUPS: readonly (readonly string[])[] = [
  ['install', 'protect', 'care', 'clean', 'fix'],
  ['buy', 'eat', 'fetch', 'drive', 'stay'],
];

/** The slugs in site order — plain list for menus without a group break. */
export const SERVICE_ORDER: readonly string[] = SERVICE_GROUPS.flat();

/**
 * The host a portal is addressed by — 'stay.torik.ai'. Always the production
 * name, also in development, where the link itself points somewhere else: the
 * label is what a human reads, and a raw dev URL reads worse.
 */
export function servicePortalHost(slug: string): string {
  return `${slug}.${portalBaseDomain}`;
}

/**
 * Link to one portal.
 *
 * Production (including the packaged desktop app) addresses the portal's own
 * subdomain. In development the apex path is used instead — the wildcard
 * `*.torik.ai` DNS record is not in place, while `/portal/<slug>` renders exactly
 * the same page, both routes being served by the same web route.
 */
export function servicePortalUrl(slug: string): string {
  return import.meta.env.PROD ? `https://${servicePortalHost(slug)}` : `${brand.siteUrl}/portal/${slug}`;
}
